import { z } from 'zod';
import { LIFT_SPEED_TILES_PER_S } from '../constants.js';

/**
 * Schémas zod des entités GÉNÉRIQUES du moteur (concept §8.2 : « le moteur
 * fournit la base commune : […] boutons/portes/ascenseurs/collectibles »).
 *
 * Chaque `entityFactory` valide sa définition avec le schéma correspondant :
 * c'est la frontière de confiance entre le JSON d'un niveau (écrit à la main
 * ou généré par une IA en phase 4) et la simulation. Les entités propres à un
 * jeu (blocs polarisés, électroaimants…) déclarent leurs schémas dans leur
 * propre package.
 */

/** Champs communs à toutes les entités : type + position en tuiles. */
export const EntityBaseSchema = z.object({
  type: z.string().min(1),
  x: z.number(),
  y: z.number(),
  /** Identifiant, requis pour être la cible d'un `linkedTo`. */
  id: z.string().min(1).optional(),
});

/** Bouton-poussoir : actif tant qu'un robot ou un objet solide le presse. */
export const ButtonDefSchema = EntityBaseSchema.extend({
  type: z.literal('button'),
  id: z.string().min(1),
  /** Largeur de la plaque, en tuiles. */
  width: z.number().positive().default(1),
});

/** Porte : solide tant que son signal est inactif. */
export const DoorDefSchema = EntityBaseSchema.extend({
  type: z.literal('door'),
  /** Identifiant du bouton / levier qui la commande. */
  linkedTo: z.string().min(1),
  width: z.number().positive().default(1),
  height: z.number().positive().default(2),
  /** `true` : la porte est ouverte au repos et se ferme quand le signal passe. */
  inverted: z.boolean().default(false),
});

/** Un point de la trajectoire d'un ascenseur, en tuiles. */
export const PathPointSchema = z.tuple([z.number(), z.number()]);

/**
 * Ascenseur / plateforme mobile.
 *
 * - avec `linkedTo` : va au dernier point tant que le signal est actif, revient
 *   au premier sinon ;
 * - sans `linkedTo` : fait des allers-retours en boucle sur son chemin.
 */
export const LiftDefSchema = EntityBaseSchema.extend({
  type: z.literal('lift'),
  id: z.string().min(1),
  path: z.array(PathPointSchema).min(2),
  linkedTo: z.string().min(1).optional(),
  width: z.number().positive().default(2),
  height: z.number().positive().default(0.4),
  speed: z.number().positive().default(LIFT_SPEED_TILES_PER_S),
});

/** Collectible ramassable par un joueur précis (ou par les deux). */
export const CollectibleDefSchema = EntityBaseSchema.extend({
  type: z.literal('collectible'),
  /** Index du joueur autorisé à le ramasser ; absent = les deux. */
  player: z.union([z.literal(0), z.literal(1)]).optional(),
});

export type ButtonDef = z.infer<typeof ButtonDefSchema>;
export type DoorDef = z.infer<typeof DoorDefSchema>;
export type LiftDef = z.infer<typeof LiftDefSchema>;
export type CollectibleDef = z.infer<typeof CollectibleDefSchema>;
export type PathPoint = z.infer<typeof PathPointSchema>;
